import { useState } from 'react';
import { Icon, type IconName } from './Icon';
import { MobileSheet } from './MobileSheet';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface ActionItem {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  /** Azione distruttiva: renderizzata in fondo, separata, rossa */
  danger?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

/**
 * Menu azioni condiviso (pattern P1/P3 del design system): un solo bottone
 * "Azioni"/kebab per le azioni secondarie di un header. Le voci danger vanno
 * in fondo, separate. `excludeKey` nasconde la voce già promossa altrove
 * (es. nella NextActionCard) così nessuna azione appare due volte.
 *
 * ⭐ SUL TELEFONO LE VOCI VANNO NEL PANNELLO DAL BASSO (MobileSheet), non nella
 * tendina. Prima era la stessa tendina, trasformata in foglio da mobile.css
 * (position: fixed, bottom: 0). Però la tendina nasce DENTRO la pagina, e
 * `.page` sul telefono ha `isolation: isolate` (mobile.css §D): è un livello
 * di sovrapposizione chiuso, e lo z-index del foglio valeva solo lì dentro.
 * Risultato misurato a 390px: il foglio si apriva ma finiva SOTTO la barra in
 * basso (z-index del guscio, fuori dalla pagina), e un menu di una o due voci
 * spariva per intero dietro di lei; il velo restava sotto la barra in alto.
 * Lo stesso danno lo fa qualunque antenato con transform, filter o
 * backdrop-filter (il vetro delle barre): diventa il riferimento di
 * position:fixed e il foglio si aggancia a lui invece che allo schermo.
 * MobileSheet sta in un portale su <body>: nessun antenato lo può più
 * chiudere dentro, qualunque cosa succeda alla pagina o alla barra in alto.
 * Sul computer invece non cambia niente: stessa tendina, stesso DOM.
 */
export function ActionsMenu({
  items,
  variant = 'ghost',
  label,
  excludeKey,
  disabled,
}: {
  items: ActionItem[];
  variant?: 'primary' | 'ghost' | 'default';
  label?: string;
  excludeKey?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const visible = items.filter((it) => !it.hidden && it.key !== excludeKey);
  if (!visible.length) return null;
  const normal = visible.filter((it) => !it.danger);
  const dangers = visible.filter((it) => it.danger);

  const btnClass = variant === 'primary' ? 'btn btn-primary' : variant === 'ghost' ? 'btn btn-ghost btn-icon' : 'btn';
  const title = label ?? 'Azioni';

  // Una voce del pannello: si chiude prima, poi si esegue, come sul computer
  // (così una conferma o un modale aperti dalla voce non finiscono sotto il
  // pannello che si sta chiudendo).
  const sheetRow = (it: ActionItem) => (
    <button
      key={it.key}
      type="button"
      role="menuitem"
      className="m-sheet-row m-act-row"
      disabled={it.disabled}
      onClick={() => { setOpen(false); it.onClick(); }}
    >
      <Icon name={it.icon} size={16} className="m-act-ico" />
      <span className="m-sheet-row-lbl">{it.label}</span>
    </button>
  );

  return (
    <div className="relative actions-menu">
      <button className={btnClass} onClick={() => setOpen((v) => !v)} disabled={disabled} aria-haspopup={isMobile ? 'dialog' : 'menu'} aria-expanded={open} title={title}>
        {variant === 'ghost'
          ? <Icon name="more" size={14} />
          : <>{title} <Icon name="chevronD" size={10} /></>}
      </button>
      {isMobile ? (
        <MobileSheet open={open} onClose={() => setOpen(false)} title={title}>
          <div role="menu" aria-label={title}>
            {normal.map(sheetRow)}
            {dangers.length > 0 && <div className="m-act-danger">{dangers.map(sheetRow)}</div>}
          </div>
        </MobileSheet>
      ) : open && (
        <>
          <div className="fixed inset-0 z-[60] actions-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] border border-line bg-white shadow-lg overflow-hidden actions-menu-pop" style={{ minWidth: 230, borderRadius: 'var(--r-card)' }} role="menu">
            {normal.map((it) => (
              <div
                key={it.key}
                className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-paper-2 cursor-pointer transition text-[13px] actions-menu-row ${it.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                role="menuitem"
                onClick={() => { setOpen(false); it.onClick(); }}
              >
                <Icon name={it.icon} size={12} className="mute" />
                <span>{it.label}</span>
              </div>
            ))}
            {dangers.length > 0 && (
              <div className="border-t border-line">
                {dangers.map((it) => (
                  <div
                    key={it.key}
                    className={`flex items-center gap-2.5 px-3 py-2.5 hover:bg-alert-bg cursor-pointer transition text-[13px] text-alert actions-menu-row ${it.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                    role="menuitem"
                    onClick={() => { setOpen(false); it.onClick(); }}
                  >
                    <Icon name={it.icon} size={12} />
                    <span>{it.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
